import type { GameView, Seat } from "@word-duel/shared";

const INDEX_KEY = "word-duel:games:v1";
const SNAPSHOT_PREFIX = "word-duel:snapshot:v1:";
const TURN_ALERTS_KEY = "word-duel:turn-alerts:v1";
const CODE_PATTERN = /^[0-9A-HJKMNP-TV-Z]{10}$/;
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,256}$/;

export interface StoredGame {
  code: string;
  token: string;
  seat: Seat | null;
  nickname: string;
  createdAt: string;
  lastOpenedAt: string;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function normalizeGameCode(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-HJKMNP-TV-Z]/g, "")
    .slice(0, 10);
}

export function isGameCode(value: string): boolean {
  return CODE_PATTERN.test(value);
}

export function listStoredGames(): StoredGame[] {
  const games = readJson<unknown>(INDEX_KEY);
  if (!Array.isArray(games)) return [];

  return games
    .filter((item): item is StoredGame => {
      if (!item || typeof item !== "object") return false;
      const candidate = item as Partial<StoredGame>;
      return (
        typeof candidate.code === "string" &&
        CODE_PATTERN.test(candidate.code) &&
        typeof candidate.token === "string" &&
        TOKEN_PATTERN.test(candidate.token) &&
        (candidate.seat === 1 || candidate.seat === 2 || candidate.seat === null) &&
        typeof candidate.nickname === "string" &&
        typeof candidate.createdAt === "string" &&
        typeof candidate.lastOpenedAt === "string"
      );
    })
    .sort((a, b) => b.lastOpenedAt.localeCompare(a.lastOpenedAt));
}

export function getStoredGame(code: string): StoredGame | null {
  const normalized = normalizeGameCode(code);
  return listStoredGames().find((game) => game.code === normalized) ?? null;
}

export function saveStoredGame(
  input: Pick<StoredGame, "code" | "token"> &
    Partial<Pick<StoredGame, "seat" | "nickname" | "createdAt">>
): StoredGame {
  const code = normalizeGameCode(input.code);
  if (!CODE_PATTERN.test(code) || !TOKEN_PATTERN.test(input.token)) {
    throw new Error("Invalid local game credentials");
  }

  const now = new Date().toISOString();
  const existing = getStoredGame(code);
  const game: StoredGame = {
    code,
    token: input.token,
    seat: input.seat ?? existing?.seat ?? null,
    nickname: input.nickname ?? existing?.nickname ?? "",
    createdAt: input.createdAt ?? existing?.createdAt ?? now,
    lastOpenedAt: now
  };
  const next = [game, ...listStoredGames().filter((item) => item.code !== code)].slice(0, 50);
  writeJson(INDEX_KEY, next);
  return game;
}

export function removeStoredGame(code: string): void {
  const normalized = normalizeGameCode(code);
  writeJson(
    INDEX_KEY,
    listStoredGames().filter((game) => game.code !== normalized)
  );
  try {
    localStorage.removeItem(`${SNAPSHOT_PREFIX}${normalized}`);
  } catch {
    // Storage may be unavailable in hardened browsing modes.
  }
  setTurnAlertsEnabled(normalized, false);
}

export function listTurnAlertGames(): string[] {
  const codes = readJson<unknown>(TURN_ALERTS_KEY);
  if (!Array.isArray(codes)) return [];
  return [
    ...new Set(
      codes.filter((code): code is string => typeof code === "string" && isGameCode(code))
    )
  ];
}

export function hasTurnAlertsEnabled(code: string): boolean {
  return listTurnAlertGames().includes(normalizeGameCode(code));
}

export function setTurnAlertsEnabled(code: string, enabled: boolean): void {
  const normalized = normalizeGameCode(code);
  if (!isGameCode(normalized)) return;
  const current = listTurnAlertGames();
  const next = enabled
    ? [...new Set([...current, normalized])]
    : current.filter((candidate) => candidate !== normalized);
  writeJson(TURN_ALERTS_KEY, next);
}

export function saveGameSnapshot(game: GameView): void {
  writeJson(`${SNAPSHOT_PREFIX}${game.code}`, { savedAt: new Date().toISOString(), game });
}

export function getGameSnapshot(code: string): { savedAt: string; game: GameView } | null {
  const cached = readJson<{ savedAt?: unknown; game?: unknown }>(
    `${SNAPSHOT_PREFIX}${normalizeGameCode(code)}`
  );
  if (
    !cached ||
    typeof cached.savedAt !== "string" ||
    !cached.game ||
    typeof cached.game !== "object"
  )
    return null;
  return cached as { savedAt: string; game: GameView };
}

export function captureResumeTokenFromFragment(): void {
  if (typeof window === "undefined" || !window.location.hash) return;

  const match = window.location.pathname.match(/^\/game\/([0-9A-HJKMNP-TV-Z]{10})\/?$/i);
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  const token = fragment.get("token");

  // Always remove fragments immediately. Credentials must never survive in browser chrome or referrers.
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}`
  );

  if (!match || !token || !TOKEN_PATTERN.test(token)) return;
  saveStoredGame({ code: match[1] ?? "", token });
}

export function makeInviteUrl(code: string): string {
  return `${window.location.origin}/join/${encodeURIComponent(normalizeGameCode(code))}`;
}

export function makeResumeUrl(code: string, token: string): string {
  return `${window.location.origin}/game/${encodeURIComponent(normalizeGameCode(code))}#token=${encodeURIComponent(token)}`;
}
